import { defineMessages } from 'react-intl';

type MessageKeys =
	| 'inProgress'
	| 'working'
	| 'needsInput'
	| 'rovoIsThinking'
	| 'agentIsThinking'
	| 'additionalAgents'
	| 'additionalAgentsLabel'
	| 'contextsLabel'
	| 'additionalContexts'
	| 'additionalContextsLabel'
	| 'compactMinute'
	| 'compactHour'
	| 'compactDay'
	| 'compactWeek'
	| 'compactYear'
	| 'unreadSessionLabel';

export const messages: Record<
	MessageKeys,
	{ id: string; defaultMessage: string; description?: string }
> = defineMessages({
	compactMinute: {
		id: 'rovo-platform-ui-components.session-details.compact-minute.ai-non-final',
		defaultMessage: '{value, number}m',
		description:
			'Compact elapsed-time label on an agent session row, in minutes (for example 14m). The value is the number of elapsed minutes. Translate the unit abbreviation and adjust spacing as needed.',
	},
	compactHour: {
		id: 'rovo-platform-ui-components.session-details.compact-hour.ai-non-final',
		defaultMessage: '{value, number}h',
		description:
			'Compact elapsed-time label on an agent session row, in hours (for example 3h). The value is the number of elapsed hours. Translate the unit abbreviation and adjust spacing as needed.',
	},
	compactDay: {
		id: 'rovo-platform-ui-components.session-details.compact-day.ai-non-final',
		defaultMessage: '{value, number}d',
		description:
			'Compact elapsed-time label on an agent session row, in days (for example 4d). The value is the number of elapsed days. Translate the unit abbreviation and adjust spacing as needed.',
	},
	compactWeek: {
		id: 'rovo-platform-ui-components.session-details.compact-week.ai-non-final',
		defaultMessage: '{value, number}w',
		description:
			'Compact elapsed-time label on an agent session row, in weeks (for example 4w). The value is the number of elapsed weeks. Translate the unit abbreviation and adjust spacing as needed.',
	},
	compactYear: {
		id: 'rovo-platform-ui-components.session-details.compact-year.ai-non-final',
		defaultMessage: '{value, number}y',
		description:
			'Compact elapsed-time label on an agent session row, in years (for example 2y). The value is the number of elapsed years. Translate the unit abbreviation and adjust spacing as needed.',
	},
	inProgress: {
		id: 'rovo-platform-ui-components.session-details.inProgress',
		defaultMessage: 'In progress',
		description: 'Accessible label for the indicator shown while a session is in progress.',
	},
	working: {
		id: 'rovo-platform-ui-components.session-details.working',
		defaultMessage: 'Working',
		description: 'Shimmering status text shown while an agent session is working.',
	},
	unreadSessionLabel: {
		// eslint-disable-next-line @atlassian/i18n/enforce-id-by-package -- Preserve the existing translation when extracting the unread indicator.
		id: 'ai-inbox.unreadSessionLabel',
		defaultMessage: 'Unread agent session',
		description: 'Accessible label for the unread indicator on an agent session.',
	},
	needsInput: {
		id: 'rovo-platform-ui-components.session-details.needs-input',
		defaultMessage: 'Needs input',
		description: 'Status text shown when a conversation or session requires user input.',
	},
	rovoIsThinking: {
		id: 'rovo-platform-ui-components.session-details.rovo-is-thinking',
		defaultMessage: 'Rovo is thinking...',
		description: 'Status text shown while the default Rovo agent is preparing a response.',
	},
	agentIsThinking: {
		id: 'rovo-platform-ui-components.session-details.agent-is-thinking',
		defaultMessage: 'Agent is thinking...',
		description: 'Status text shown while a non-default agent is preparing a response.',
	},
	additionalAgents: {
		id: 'rovo-platform-ui-components.session-details.additional-agents',
		defaultMessage: '+{count} more',
		description:
			'Visible label that indicates how many additional agents participated in a session.',
	},
	additionalAgentsLabel: {
		id: 'rovo-platform-ui-components.session-details.additional-agents-label',
		defaultMessage: '{count} more agents',
		description:
			'Accessible label that indicates how many additional agents participated in a session.',
	},
	contextsLabel: {
		id: 'rovo-platform-ui-components.session-details.contextsLabel',
		defaultMessage: 'Session contexts',
		description: 'Accessible label for the full list of contexts associated with a session.',
	},
	additionalContexts: {
		id: 'rovo-platform-ui-components.session-details.additionalContexts',
		defaultMessage: '+{count}',
		description:
			'Visible label that indicates how many additional context items are associated with a session.',
	},
	additionalContextsLabel: {
		id: 'rovo-platform-ui-components.session-details.additionalContextsLabel',
		defaultMessage: '{count, plural, one {# more context item} other {# more context items}}',
		description:
			'Accessible label that indicates how many additional context items are associated with a session.',
	},
});
